import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Form, InputGroup, Collapse } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '@mui/material/useMediaQuery';
import '../scss/faq-page.scss';
import { faqData, faqPension, faqData3 } from '../constant/faq-question.constant';
import { getItem } from '../helper/localstorage.helper';
import { useHighlightText } from '../utils/highlight.utils';



interface FAQPageProps {
  title?: string;
  subtitle?: string;
  searchPlaceholder?: string;
  searchQuery?: string;
}

export type { FAQPageProps };



export const FAQPage: React.FC<FAQPageProps> = ({
  title = "FAQ_PAGE_TITLE",
  subtitle = "FAQ_PAGE_SUBTITLE",
  searchPlaceholder = "FAQ_SEARCH_PLACEHOLDER",
  searchQuery = ""
}) => {
  const { t } = useTranslation();
  const [openItems, setOpenItems] = useState<string[]>([]); // Start with no items expanded
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isLoggedIn = JSON.parse(getItem('user'));
  
  const highlight = useHighlightText(searchQuery, '#C2BDDE');

  const filteredFAQs = searchQuery.trim() === '' ? faqData : faqData.filter(faq => {
    const searchTerm = searchQuery.toLowerCase().trim();
    const keywords = searchTerm.split(/\s+/).filter(word => word.length > 0);
    
    // Function to check if any keyword is present in a text
    const containsKeyword = (text: string) => {
      const lowerText = text.toLowerCase();
      return keywords.some(keyword => lowerText.includes(keyword));
    };
    
    // Search in question and answer
    if (containsKeyword(t(faq.question)) || containsKeyword(t(faq.answer))) {
      return true;
    }
    
    // Search in paragraphs
    const paragraphs = [faq.para1, faq.para2, faq.para3, faq.para4, faq.para5, faq.para6, faq.para7, faq.para8, faq.para9, faq.subDescription];
    for (const para of paragraphs) {
      if (para && typeof para === 'string' && containsKeyword(t(para))) {
        return true;
      }
    }
    
    // Search in headings
    const headings = [faq.heading1, faq.heading2, faq.heading, faq.head];
    for (const heading of headings) {
      if (heading && typeof heading === 'string' && containsKeyword(t(heading))) {
        return true;
      }
    }
    
    // Search in list items
    const lists = [faq.li1, faq.li2];
    for (const list of lists) {
      if (list) {
        for (const item of list) {
          if (typeof item === 'string' && containsKeyword(t(item))) {
            return true;
          }
        }
      }
    }
    
    return false;
  });

  const filteredPensionFAQs = searchQuery.trim() === '' ? faqPension : faqPension.filter(faq => {
    const searchTerm = searchQuery.toLowerCase().trim();
    const keywords = searchTerm.split(/\s+/).filter(word => word.length > 0);
    
    // Function to check if any keyword is present in a text
    const containsKeyword = (text: string) => {
      const lowerText = text.toLowerCase();
      return keywords.some(keyword => lowerText.includes(keyword));
    };
    
    // Search in title and description
    if (containsKeyword(t(faq.title)) || containsKeyword(t(faq.description))) {
      return true;
    }
    
    // Search in paragraphs
    const paragraphs = [faq.para1, faq.para2, faq.para3, faq.para4, faq.para5, faq.para6, faq.para7, faq.para8, faq.para9, faq.subDescription];
    for (const para of paragraphs) {
      if (para && typeof para === 'string' && containsKeyword(t(para))) {
        return true;
      }
    }
    
    // Search in list items
    const lists = [faq.li1, faq.li2];
    for (const list of lists) {
      if (list) {
        for (const item of list) {
          if (typeof item === 'string' && containsKeyword(t(item))) {
            return true;
          }
        }
      }
    }
    
    return false;
  });

  const filteredFAQData3 = searchQuery.trim() === '' ? faqData3 : faqData3.filter(faq => {
    const searchTerm = searchQuery.toLowerCase().trim();
    const keywords = searchTerm.split(/\s+/).filter(word => word.length > 0);
    
    // Function to check if any keyword is present in a text
    const containsKeyword = (text: string) => {
      const lowerText = text.toLowerCase();
      return keywords.some(keyword => lowerText.includes(keyword));
    };
    
    // Search in title and description
    if (containsKeyword(t(faq.title)) || containsKeyword(t(faq.description))) {
      return true;
    }
    
    // Search in paragraphs
    const paragraphs = [faq.para1, faq.para2, faq.para3, faq.para4, faq.para5, faq.para6, faq.para7, faq.para8, faq.para9, faq.subDescription];
    for (const para of paragraphs) {
      if (para && typeof para === 'string' && containsKeyword(t(para))) {
        return true;
      }
    }
    
    // Search in headings
    const headings = [faq.heading1, faq.heading2, faq.heading, faq.head];
    for (const heading of headings) {
      if (heading && typeof heading === 'string' && containsKeyword(t(heading))) {
        return true;
      }
    }
    
    // Search in list items
    const lists = [faq.li1, faq.li2];
    for (const list of lists) {
      if (list) {
        for (const item of list) {
          if (typeof item === 'string' && containsKeyword(t(item))) {
            return true;
          }
        }
      }
    }
    
    return false;
  });

  const toggleItem = (id: string) => {
    setOpenItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id) // Close the item if it's already open
        : [id] // Close all others and open only this one
    );
  };

  const categoryTitles = {
    general: 'FAQ_GENERAL_CATEGORY',
    login: 'FAQ_LOGIN_CATEGORY', 
    // features: 'FAQ_FEATURES_CATEGORY',
    featuresExtended: 'FAQ_FEATURES_CATEGORY',
    pension: 'Pension'
  };

  const renderFAQSection = (category: 'general' | 'login' ) => {
    const categoryFAQs = filteredFAQs.filter(faq => faq.category === category);
    
    if (categoryFAQs.length === 0) return null;

    return (
      <div key={category} className="faq-page-container mb-120px">
        <Row>
          <Col xs={12} lg={5} className="mb-120px mb-lg-0 px-0">
            <div className="faq-category-title">
              <h2 className="h2-bold text-gray-900 mb-0">
                {t(categoryTitles[category])}
              </h2>
            </div>
          </Col>
          <Col xs={12} lg={7} className="px-0">
            <div className="faq-items">
              {categoryFAQs.map((faq, index) => (
                <div key={faq.id} className={`faq-item ${index !== categoryFAQs.length - 1 ? 'border-bottom' : ''}`}>
                  <div 
                    className="faq-item-header"
                    onClick={() => toggleItem(faq.id)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => e.key === 'Enter' && toggleItem(faq.id)}
                  >
                    <div className="d-flex align-items-center justify-content-between py-0">
                      <h5 className="h5-bold text-gray-900 mb-0 pe-3">
                        {highlight(t(faq.question))}
                      </h5>
                      <div className={`faq-chevron ${openItems.includes(faq.id) ? 'rotated' : ''}`}>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                          <circle cx="16" cy="16" r="15" stroke="#252B37" strokeWidth="1"/>
                          <path 
                            d="M12 14L16 18L20 14" 
                            stroke="#252B37" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <Collapse in={openItems.includes(faq.id)}>
                    <div className="faq-item-body px-0 pt-0 pb-1">
                      <div className="body-text text-gray-700 pe-5 mr-10 mw-100">
                        <p className="mb-3" style={{ whiteSpace: 'pre-line' }}>
                          {highlight(t(faq.answer))}
                        </p>
                        {faq.subDescription && <p className="mb-3">{highlight(t(faq.subDescription))}</p>}
                        {faq.heading1 && <h6 className="h6-bold mb-2">{highlight(t(faq.heading1))}</h6>}
                        {faq.para1 && <p className="mb-2">{highlight(typeof faq.para1 === 'string' ? t(faq.para1) : faq.para1)}</p>}
                        {faq.supportEmail && (
                          <a href={faq.support} className="text-decoration-underline mb-2 d-inline-block">
                            {faq.supportEmail}
                          </a>
                        )}
                        {faq.para2 && <p className="mb-2">{highlight(typeof faq.para2 === 'string' ? t(faq.para2) : faq.para2)}</p>}
                        {faq.para3 && <p className="mb-2">{highlight(typeof faq.para3 === 'string' ? t(faq.para3) : faq.para3)}</p>}
                        {faq.li1 && (
                          <ul className="mb-3">
                            {faq.li1.map((item, liIndex) => (
                              <li key={liIndex}>{highlight(t(item))}</li>
                            ))}
                          </ul>
                        )}
                        {faq.heading2 && <h6 className="h6-bold mb-2">{highlight(t(faq.heading2))}</h6>}
                        {faq.para4 && <p className="mb-2">{highlight(typeof faq.para4 === 'string' ? t(faq.para4) : faq.para4)}</p>}
                        {faq.para5 && <p className="mb-2">{highlight(typeof faq.para5 === 'string' ? t(faq.para5) : faq.para5)}</p>}
                        {faq.para6 && <p className="mb-2">{highlight(typeof faq.para6 === 'string' ? t(faq.para6) : faq.para6)}</p>}
                        {faq.para7 && <p className="mb-2">{highlight(typeof faq.para7 === 'string' ? t(faq.para7) : faq.para7)}</p>}
                        {faq.para8 && <p className="mb-2">{highlight(typeof faq.para8 === 'string' ? t(faq.para8) : faq.para8)}</p>}
                        {faq.supportEmail2 && (
                          <a href={faq.support2} className="text-decoration-underline mb-2 d-inline-block">
                            {faq.supportEmail2}
                          </a>
                        )}
                        {faq.para9 && <p className="mb-2">{highlight(typeof faq.para9 === 'string' ? t(faq.para9) : faq.para9)}</p>}
                        {faq.link && !(faq.id === '58' && !isLoggedIn) && (
                          <a href={faq.redirect} target="_blank" rel="noopener noreferrer" className="text-decoration-underline mb-2 d-inline-block">
                            {highlight(t(faq.link))}
                          </a>
                        )}
                        {faq.head && <h6 className="h6-bold mb-2">{highlight(t(faq.head))}</h6>}
                        {faq.li2 && (
                          <ul className="mb-3">
                            {faq.li2.map((item, liIndex) => (
                              <li key={liIndex}>{highlight(t(item))}</li>
                            ))}
                          </ul>
                        )}
                        {faq.supportEmail1 && (
                          <a href={faq.support1} className="text-decoration-underline mb-0 d-inline-block">
                            {faq.supportEmail1}
                          </a>
                        )}
                      </div>
                    </div>
                  </Collapse>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  const renderPensionSection = () => {
    if (filteredPensionFAQs.length === 0) return null;

    return (
      <div key="pension" className="faq-page-container mb-120px">
        <Row>
          <Col xs={12} lg={5} className="mb-120px mb-lg-0 px-0">
            <div className="faq-category-title">
              <h2 className="h2-bold text-gray-900 mb-0">
                {t(categoryTitles.pension)}
              </h2>
            </div>
          </Col>
          <Col xs={12} lg={7} className="px-0">
            <div className="faq-items">
              {filteredPensionFAQs.map((faq, index) => (
                <div key={`pension-${index}`} className={`faq-item ${index !== filteredPensionFAQs.length - 1 ? 'border-bottom' : ''}`}>
                  <div 
                    className="faq-item-header"
                    onClick={() => toggleItem(`pension-${index}`)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => e.key === 'Enter' && toggleItem(`pension-${index}`)}
                  >
                    <div className="d-flex align-items-center justify-content-between py-0">
                      <h5 className="h5-bold text-gray-900 mb-0 pe-3">
                        {highlight(t(faq.title))}
                      </h5>
                      <div className={`faq-chevron ${openItems.includes(`pension-${index}`) ? 'rotated' : ''}`}>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                          <circle cx="16" cy="16" r="15" stroke="#252B37" strokeWidth="1"/>
                          <path 
                            d="M12 14L16 18L20 14" 
                            stroke="#252B37" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <Collapse in={openItems.includes(`pension-${index}`)}>
                    <div className="faq-item-body px-0 pt-0 pb-1">
                      <div className="body-text text-gray-700 pe-5 mr-10 mw-100">
                        <p className="mb-3">{highlight(t(faq.description))}</p>
                        {faq.para1 && <p className="mb-2">{highlight(t(faq.para1))}</p>}
                        {faq.li2 && (
                          <ul className="mb-3">
                            {faq.li2.map((item, liIndex) => (
                              <li key={liIndex}>{highlight(t(item))}</li>
                            ))}
                          </ul>
                        )}
                        {faq.para9 && <p className="mb-2">{highlight(t(faq.para9))}</p>}
                        {faq.para4 && <p className="mb-3">{highlight(t(faq.para4))}</p>}
                        {faq.para5 && <p className="mb-0">{highlight(t(faq.para5))}</p>}
                      </div>
                    </div>
                  </Collapse>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  const renderFeaturesExtendedSection = () => {
    if (filteredFAQData3.length === 0) return null;

    return (
      <div key="featuresExtended" className="faq-page-container mb-120px">
        <Row>
          <Col xs={12} lg={5} className="mb-120px mb-lg-0 px-0">
            <div className="faq-category-title">
              <h2 className="h2-bold text-gray-900 mb-0">
                {t(categoryTitles.featuresExtended)}
              </h2>
            </div>
          </Col>
          <Col xs={12} lg={7} className="px-0">
            <div className="faq-items">
              {filteredFAQData3.map((faq, index) => (
                <div key={`features-extended-${index}`} className={`faq-item ${index !== filteredFAQData3.length - 1 ? 'border-bottom' : ''}`}>
                  <div 
                    className="faq-item-header"
                    onClick={() => toggleItem(`features-extended-${index}`)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => e.key === 'Enter' && toggleItem(`features-extended-${index}`)}
                  >
                    <div className="d-flex align-items-center justify-content-between py-0">
                      <h5 className="h5-bold text-gray-900 mb-0 pe-3">
                        {highlight(t(faq.title))}
                      </h5>
                      <div className={`faq-chevron ${openItems.includes(`features-extended-${index}`) ? 'rotated' : ''}`}>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                          <circle cx="16" cy="16" r="15" stroke="#252B37" strokeWidth="1"/>
                          <path 
                            d="M12 14L16 18L20 14" 
                            stroke="#252B37" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <Collapse in={openItems.includes(`features-extended-${index}`)}>
                    <div className="faq-item-body px-0 pt-0 pb-1">
                      <div className="body-text text-gray-700 pe-5 mr-10 mw-100">
                        <p className="mb-3">
                          {highlight(t(faq.description))}
                          {faq.supportEmail && (
                            <>
                              {' '}
                              <a href={faq.support || `mailto:${faq.supportEmail}`} className="text-primary">
                                {faq.supportEmail}
                              </a>
                            </>
                          )}
                          {faq.span && <span> {highlight(t(faq.span))}</span>}
                        </p>

                        {faq.heading && <h6 className="mb-2 fw-bold">{highlight(t(faq.heading))}</h6>}
                        {faq.heading1 && <h6 className="mb-2 fw-bold">{highlight(t(faq.heading1))}</h6>}
                        {faq.heading2 && <h6 className="mb-2 fw-bold">{highlight(t(faq.heading2))}</h6>}

                        {faq.para1 && <p className="mb-2">{highlight(faq.para1.startsWith('FAQ_') ? t(faq.para1) : faq.para1)}</p>}
                         {faq.li1 && (
                          <ul className="mb-3">
                            {faq.li1.map((item, liIndex) => {
                              const text = item.startsWith('FAQ_') ? t(item) : item;
                              return <li key={liIndex}>{highlight(text)}</li>;
                            })}
                          </ul>
                        )}
                        {faq.para2 && <p className="mb-2">{highlight(faq.para2.startsWith('FAQ_') ? t(faq.para2) : faq.para2)}</p>}
                        {faq.para3 && <p className="mb-2">{highlight(faq.para3.startsWith('FAQ_') ? t(faq.para3) : faq.para3)}</p>}
                        {faq.para4 && <p className="mb-2">{highlight(faq.para4.startsWith('FAQ_') ? t(faq.para4) : faq.para4)}</p>}
                        {faq.para5 && <p className="mb-2">{highlight(faq.para5.startsWith('FAQ_') ? t(faq.para5) : faq.para5)}</p>}
                        {faq.para6 && <p className="mb-2">{highlight(faq.para6.startsWith('FAQ_') ? t(faq.para6) : faq.para6)}</p>}
                        {faq.para7 && <p className="mb-2">{highlight(faq.para7.startsWith('FAQ_') ? t(faq.para7) : faq.para7)}</p>}
                        {faq.para8 && <p className="mb-2">{highlight(faq.para8.startsWith('FAQ_') ? t(faq.para8) : faq.para8)}</p>}
                        {faq.para9 && <p className="mb-2">{highlight(faq.para9.startsWith('FAQ_') ? t(faq.para9) : faq.para9)}</p>}
                        
                        
                        
                        {faq.li2 && (
                          <ul className="mb-3">
                            {faq.li2.map((item, liIndex) => {
                              const text = item.startsWith('FAQ_') ? t(item) : item;
                              return <li key={liIndex}>{highlight(text)}</li>;
                            })}
                          </ul>
                        )}
                        
                        {faq.link && (
                          <p className="mb-2">
                            <a href={faq.redirect} target="_blank" rel="noopener noreferrer" className="text-primary">
                              {highlight(t(faq.link))}
                            </a>
                          </p>
                        )}
                        
                        {faq.optionalLinks && (
                          <div className="mb-2">
                            {faq.optionalLinks.map((linkItem, linkIndex) => (
                              <p key={linkIndex} className="mb-1">
                                <a href={linkItem.redirect} target="_blank" rel="noopener noreferrer" className="text-primary">
                                  {linkItem.link}
                                </a>
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </Collapse>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <div className="faq-page">
      <section className="faq-content py-5">
        <Container>
          {/* Show no results message when searching and no results found */}
          {searchQuery.trim() !== '' && 
           filteredFAQs.length === 0 && 
           filteredPensionFAQs.length === 0 && 
           filteredFAQData3.length === 0 && (
            <div className="text-center py-5">
              <h4 className="text-gray-700">{t('NO_SEARCH_RESULTS')}</h4>
            </div>
          )}
          
          {renderFAQSection('general')}
          {renderFAQSection('login')}
          {renderFeaturesExtendedSection()}
          {renderPensionSection()}
        </Container>
      </section>
    </div>
  );
};

export default FAQPage;
